// src/Pages/Load/LoadContainer/modals/ViewGatePassModal.jsx

"use client";

import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "sonner";
import {
  Eye,
  Download,
  Trash2,
  FileText as FileTextIcon,
  Loader2,
} from "lucide-react";

import { Button } from "../../../../Components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogHeader,
} from "../../../../Components/ui/dialog";

export default function ViewGatePassModal({
  isOpen,
  onClose,
  loadItems,
  selectedLoad,
  userRoleId,
  allowedRolesToDelete = [1, 2, 4, 7],
}) {
  const [selectedLoadItem, setSelectedLoadItem] = useState(null);
  const [gatePasses, setGatePasses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedGatePass, setSelectedGatePass] = useState(null);
  const [fullLoadData, setFullLoadData] = useState(null);
  const [deletingGatePassId, setDeletingGatePassId] = useState(null);

  useEffect(() => {
    if (isOpen && selectedLoad) {
      fetchLoadTransportData(selectedLoad.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedLoad, loadItems]);

  const fetchLoadTransportData = async (loadTransportId) => {
    setIsLoading(true);
    try {
      const userToken = localStorage.getItem("token");
      const response = await axios.get(
        `https://new.cajuu.app/api/loadtransport/${loadTransportId}`,
        {
          headers: {
            "Screen-Name": "/Load",
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      if (response.data && response.data.data && response.data.data.load_items) {
        const loadItemsData = response.data.data.load_items;
        setFullLoadData(response.data.data);

        // Sempre selecionar o primeiro veículo quando o modal abrir
        const firstItem = loadItemsData[0];
        setSelectedLoadItem(firstItem);

        const gatePassesData = firstItem.gate_passes || [];
        setGatePasses(gatePassesData);
        if (gatePassesData.length > 0) {
          setSelectedGatePass(gatePassesData[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching load transport data", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch load transport data.",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVehicleChange = (loadItemId) => {
    let selectedItem = null;

    // Primeiro tenta encontrar no fullLoadData (dados completos da API)
    if (fullLoadData && fullLoadData.load_items) {
      selectedItem = fullLoadData.load_items.find(
        (item) => item.id.toString() === loadItemId
      );
    }

    // Se não encontrou, tenta nos loadItems originais
    if (!selectedItem && loadItems) {
      selectedItem = loadItems.find((item) => item.id.toString() === loadItemId);
    }

    if (selectedItem) {
      setSelectedLoadItem(selectedItem);
      const gatePassesData = selectedItem.gate_passes || [];
      setGatePasses(gatePassesData);
      if (gatePassesData.length > 0) {
        setSelectedGatePass(gatePassesData[0]);
      } else {
        setSelectedGatePass(null);
      }
    }
  };

  // Função para obter a lista de veículos a ser exibida no select
  const getVehicleOptions = () => {
    // Prioriza os dados completos da API
    if (fullLoadData && fullLoadData.load_items) {
      return fullLoadData.load_items;
    }
    // Fallback para os loadItems originais
    return loadItems || [];
  };

  const handleSelectGatePass = (gatePass) => {
    setSelectedGatePass(gatePass);
  };

  const handleViewPdf = (pdfPath) => {
    if (pdfPath) {
      const fullUrl = pdfPath.startsWith("http")
        ? pdfPath
        : `https://new.cajuu.app/storage/${pdfPath}`;
      window.open(fullUrl, "_blank");
    }
  };

  const handleDownloadPdf = async (gatePassId, fileName) => {
    try {
      const userToken = localStorage.getItem("token");
      const response = await axios.get(
        `https://new.cajuu.app/api/gate_pass/${gatePassId}/download`,
        {
          headers: {
            "Screen-Name": "/Load",
            Authorization: `Bearer ${userToken}`,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || `gate-pass-${gatePassId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Gate pass downloaded successfully!");
    } catch (error) {
      console.error("Error downloading PDF", error);
      toast.error("Failed to download gate pass.");
    }
  };

  const handleDeletePdf = async (gatePassId) => {
    setDeletingGatePassId(gatePassId);

    try {
      const userToken = localStorage.getItem("token");

      const response = await axios.delete(
        `https://new.cajuu.app/api/loaditem/gate_pass/${gatePassId}`,
        {
          headers: {
            "Screen-Name": "/Load",
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      console.log("✅ Resposta da API:", response.data);
      console.log("✅ Status da resposta:", response.status);

      const updatedGatePasses = gatePasses.filter((gp) => gp.id !== gatePassId);
      setGatePasses(updatedGatePasses);

      if (selectedGatePass && selectedGatePass.id === gatePassId) {
        setSelectedGatePass(updatedGatePasses.length > 0 ? updatedGatePasses[0] : null);
      }

      if (fullLoadData && fullLoadData.load_items) {
        const updatedLoadItems = fullLoadData.load_items.map((item) => {
          if (item.gate_passes && item.gate_passes.some((gp) => gp.id === gatePassId)) {
            return {
              ...item,
              gate_passes: item.gate_passes.filter((gp) => gp.id !== gatePassId),
            };
          }
          return item;
        });

        setFullLoadData({
          ...fullLoadData,
          load_items: updatedLoadItems,
        });
      }

      Swal.fire({
        title: "Deleted!",
        text: "Gate pass has been deleted successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      let errorMessage = "Failed to delete gate pass.";

      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = "Unauthorized. Please login again.";
        } else if (error.response.status === 404) {
          errorMessage = "Gate pass not found. It may have already been deleted.";
        } else if (error.response.status === 403) {
          errorMessage = "You don't have permission to delete this gate pass.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
          console.error("❌ Server error details:", error.response.data);
        }
      } else if (error.code === "ECONNABORTED") {
        errorMessage = "Request timeout. Please try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });

      if (error.response?.status === 500) {
        console.error("❌ Detalhes do erro 500:", error.response.data);
      }
    } finally {
      setDeletingGatePassId(null);
    }
  };

  const vehicleOptions = getVehicleOptions();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[85vh] sm:max-w-[95vw] sm:h-[90vh] sm:mx-2 sm:px-4">
        <DialogHeader className="pb-2 space-y-1">
          <DialogTitle className="text-lg sm:text-xl">View Gate Pass</DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            View and manage gate pass documents for vehicles in order #{selectedLoad?.order_id}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col h-full">
          {vehicleOptions.length > 1 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-white mb-1 sm:text-base">
                Select Vehicle
              </label>
              <select
                value={selectedLoadItem?.id || ""}
                onChange={(e) => handleVehicleChange(e.target.value)}
                className="w-full p-2 rounded bg-zinc-800 border border-zinc-600 text-white text-sm sm:text-base sm:p-3"
              >
                {vehicleOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.year} {item.make} {item.model}
                    {item.gate_passes && item.gate_passes.length > 0
                      ? ` (${item.gate_passes.length} gate pass${
                          item.gate_passes.length > 1 ? "es" : ""
                        })`
                      : " (No gate passes)"}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4 sm:h-12 sm:w-12"></div>
                <p className="sm:text-lg">Loading gate passes...</p>
              </div>
            </div>
          ) : !selectedLoadItem ? (
            <div className="flex-1 flex items-center justify-center text-zinc-400 sm:text-lg">
              Select a vehicle to view gate passes
            </div>
          ) : gatePasses.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
              <FileTextIcon className="h-16 w-16 mb-4 opacity-40 sm:h-20 sm:w-20" />
              <p className="sm:text-lg">No gate pass found for this vehicle.</p>
              <p className="text-sm mt-1 sm:text-base">
                {selectedLoadItem.year} {selectedLoadItem.make} {selectedLoadItem.model}
              </p>
            </div>
          ) : (
            <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
              <div className="w-full md:w-1/3 md:pr-4 md:border-r md:border-zinc-700 overflow-y-auto">
                <div className="space-y-3 sm:space-y-4">
                  {gatePasses.map((gatePass) => (
                    <div
                      key={gatePass.id}
                      className={`p-4 rounded-lg cursor-pointer transition-colors sm:p-5 ${
                        selectedGatePass?.id === gatePass.id
                          ? "bg-yellow-600 text-white"
                          : "bg-zinc-800 hover:bg-zinc-700 text-white"
                      }`}
                      onClick={() => handleSelectGatePass(gatePass)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium mb-2 text-sm sm:text-base sm:mb-3">
                            {gatePass.description || `Gate Pass ${gatePass.id}`}
                          </div>
                          <div className="text-xs opacity-80 sm:text-sm">
                            Uploaded on{" "}
                            {new Date(gatePass.created_at).toLocaleDateString("en-US", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                          </div>
                        </div>

                        <div className="flex gap-2 ml-3">
                          <Button
                            variant="ghost"
                            className="h-10 w-10 p-2 sm:h-8 sm:w-8 sm:p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewPdf(gatePass.path);
                            }}
                          >
                            <Eye className="h-6 w-6 sm:h-4 sm:w-4" />
                          </Button>

                          <Button
                            variant="ghost"
                            className="h-10 w-10 p-2 sm:h-8 sm:w-8 sm:p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadPdf(
                                gatePass.id,
                                gatePass.description || `gate-pass-${gatePass.id}.pdf`
                              );
                            }}
                          >
                            <Download className="h-6 w-6 sm:h-4 sm:w-4" />
                          </Button>

                          {allowedRolesToDelete.includes(userRoleId) && (
                            <Button
                              variant="ghost"
                              className="h-10 w-10 p-2 text-red-400 hover:text-red-300 sm:h-8 sm:w-8 sm:p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePdf(gatePass.id);
                              }}
                              disabled={deletingGatePassId === gatePass.id}
                            >
                              {deletingGatePassId === gatePass.id ? (
                                <Loader2 className="h-6 w-6 animate-spin sm:h-4 sm:w-4" />
                              ) : (
                                <Trash2 className="h-6 w-6 sm:h-4 sm:w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="hidden md:flex md:w-2/3 pl-4 flex-col">
                {selectedGatePass ? (
                  <>
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold text-white">
                        {selectedGatePass.description || `Gate Pass ${selectedGatePass.id}`}
                      </h3>
                      <p className="text-sm text-zinc-400">
                        Uploaded on{" "}
                        {new Date(selectedGatePass.created_at).toLocaleDateString("en-US", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </p>
                    </div>

                    <div className="flex-1 bg-zinc-900 rounded-lg overflow-hidden">
                      <div className="h-full flex items-center justify-center p-8">
                        <div className="text-center">
                          <FileTextIcon className="h-20 w-20 mx-auto mb-4 text-zinc-500" />
                          <p className="text-zinc-400 mb-4">
                            Preview not available. Click "Open" to view the PDF in a new tab.
                          </p>

                          <div className="flex gap-4 justify-center">
                            <Button
                              variant="outline"
                              onClick={() => handleViewPdf(selectedGatePass.path)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Open PDF
                            </Button>

                            <Button
                              variant="outline"
                              onClick={() =>
                                handleDownloadPdf(
                                  selectedGatePass.id,
                                  selectedGatePass.description ||
                                    `gate-pass-${selectedGatePass.id}.pdf`
                                )
                              }
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </Button>

                            {allowedRolesToDelete.includes(userRoleId) && (
                              <Button
                                variant="outline"
                                onClick={() => handleDeletePdf(selectedGatePass.id)}
                                className="text-red-400 hover:text-red-300"
                                disabled={deletingGatePassId === selectedGatePass.id}
                              >
                                {deletingGatePassId === selectedGatePass.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4 mr-2" />
                                )}
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-zinc-400">
                    Select a gate pass to view details
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button
              onClick={onClose}
              variant="outline"
              className="text-sm sm:text-base sm:px-6 sm:py-3"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}